import React, { useState, useEffect } from "react";
import { supabase } from "../config/supabase";

interface ErrorReport {
  id: string;
  user_id: string;
  report_text: string;
  created_at: string;
  status: "pending" | "reviewed" | "resolved" | "dismissed";
  admin_notes?: string;
  updated_at: string;
  user_name?: string;
  user_email?: string;
}

const ErrorReports: React.FC = () => {
  const [reports, setReports] = useState<ErrorReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<ErrorReport | null>(null);
  const [filter, setFilter] = useState<"all" | "pending" | "reviewed" | "resolved" | "dismissed">("all");
  const [adminNotes, setAdminNotes] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    fetchReports();
  }, [filter]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      // First, fetch error reports
      let query = supabase
        .from("error_reports")
        .select("*")
        .order("created_at", { ascending: false });

      // Apply filter
      if (filter !== "all") {
        query = query.eq("status", filter);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching error reports:", error);
        setError("Failed to load error reports");
        return;
      }

      // Fetch user data for each report
      const userIds = Array.from(new Set((data || []).map((r: any) => r.user_id)));
      const { data: userData } = await supabase
        .from("user_data")
        .select("user_id, name, surname, email")
        .in("user_id", userIds);

      // Create a map of user_id to user data
      const userMap = new Map(
        (userData || []).map((user: any) => [
          user.user_id,
          {
            name: user.name || "",
            surname: user.surname || "",
            email: user.email || "",
          },
        ])
      );

      // Transform data to include user info
      const transformedReports = (data || []).map((report: any) => {
        const user = userMap.get(report.user_id);
        return {
          ...report,
          user_name: user
            ? `${user.name} ${user.surname || ""}`.trim() || "Unknown User"
            : "Unknown User",
          user_email: user?.email || "No email",
        };
      });

      setReports(transformedReports);
    } catch (err) {
      console.error("Error fetching error reports:", err);
      setError("Failed to load error reports");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const handleReportClick = (report: ErrorReport) => {
    setSelectedReport(report);
    setAdminNotes(report.admin_notes || "");
  };

  const closeModal = () => {
    setSelectedReport(null);
    setAdminNotes("");
  };

  const updateReportStatus = async (newStatus: ErrorReport["status"]) => {
    if (!selectedReport) return;

    setUpdatingStatus(true);
    try {
      const { error } = await supabase
        .from("error_reports")
        .update({
          status: newStatus,
          admin_notes: adminNotes.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedReport.id);

      if (error) {
        console.error("Error updating report:", error);
        alert("Failed to update report status");
        return;
      }

      // Refresh reports
      await fetchReports();
      closeModal();
    } catch (err) {
      console.error("Error updating report:", err);
      alert("Failed to update report status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusColor = (status: ErrorReport["status"]) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "reviewed":
        return "bg-blue-100 text-blue-800";
      case "resolved":
        return "bg-green-100 text-green-800";
      case "dismissed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusCount = (status: ErrorReport["status"]) => {
    return reports.filter((r) => r.status === status).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-dark-blue"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 mb-4">{error}</div>
        <button
          onClick={fetchReports}
          className="bg-brand-dark-blue text-white px-4 py-2 rounded-lg hover:bg-brand-cyan transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  const filteredReports = filter === "all" ? reports : reports.filter((r) => r.status === filter);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Error Reports</h1>
        <button
          onClick={fetchReports}
          className="bg-brand-dark-blue text-white px-4 py-2 rounded-lg hover:bg-brand-cyan transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setFilter("all")}
          className={`px-4 py-2 rounded-lg transition-colors ${
            filter === "all"
              ? "bg-brand-dark-blue text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          All ({reports.length})
        </button>
        <button
          onClick={() => setFilter("pending")}
          className={`px-4 py-2 rounded-lg transition-colors ${
            filter === "pending"
              ? "bg-yellow-500 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          Pending ({getStatusCount("pending")})
        </button>
        <button
          onClick={() => setFilter("reviewed")}
          className={`px-4 py-2 rounded-lg transition-colors ${
            filter === "reviewed"
              ? "bg-blue-500 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          Reviewed ({getStatusCount("reviewed")})
        </button>
        <button
          onClick={() => setFilter("resolved")}
          className={`px-4 py-2 rounded-lg transition-colors ${
            filter === "resolved"
              ? "bg-green-500 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          Resolved ({getStatusCount("resolved")})
        </button>
        <button
          onClick={() => setFilter("dismissed")}
          className={`px-4 py-2 rounded-lg transition-colors ${
            filter === "dismissed"
              ? "bg-gray-500 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          Dismissed ({getStatusCount("dismissed")})
        </button>
      </div>

      {filteredReports.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p className="text-lg">No error reports found.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredReports.map((report) => (
            <div
              key={report.id}
              onClick={() => handleReportClick(report)}
              className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {report.user_name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{report.user_email}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                      report.status
                    )}`}
                  >
                    {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDate(report.created_at)}
                  </span>
                </div>
              </div>
              <p className="text-gray-700 dark:text-gray-300 line-clamp-2">
                {report.report_text.length > 150
                  ? `${report.report_text.substring(0, 150)}...`
                  : report.report_text}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Report Detail Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedReport.user_name}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">{selectedReport.user_email}</p>
                </div>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="mb-4 flex items-center gap-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Submitted: {formatDate(selectedReport.created_at)}
                </span>
                <span
                  className={`px-3 py-1 rounded text-sm font-medium ${getStatusColor(
                    selectedReport.status
                  )}`}
                >
                  {selectedReport.status.charAt(0).toUpperCase() + selectedReport.status.slice(1)}
                </span>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Error Report:</h3>
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {selectedReport.report_text}
                </p>
              </div>

              {/* Admin Notes Section */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Admin Notes:
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  rows={4}
                  placeholder="Add notes about this error report..."
                />
              </div>

              {/* Status Update Buttons */}
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  onClick={() => updateReportStatus("pending")}
                  disabled={updatingStatus || selectedReport.status === "pending"}
                  className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Mark as Pending
                </button>
                <button
                  onClick={() => updateReportStatus("reviewed")}
                  disabled={updatingStatus || selectedReport.status === "reviewed"}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Mark as Reviewed
                </button>
                <button
                  onClick={() => updateReportStatus("resolved")}
                  disabled={updatingStatus || selectedReport.status === "resolved"}
                  className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Mark as Resolved
                </button>
                <button
                  onClick={() => updateReportStatus("dismissed")}
                  disabled={updatingStatus || selectedReport.status === "dismissed"}
                  className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Dismiss
                </button>
                <button
                  onClick={closeModal}
                  className="bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-white px-4 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorReports;

